/**
 * SidebarSlider
 * Minimal dot-based image slider for the blog sidebar widget.
 * Auto-advances every 5 s. Respects prefers-reduced-motion.
 */
class SidebarSlider extends HTMLElement {
  /** @type {HTMLElement|null} */ #track = null;
  /** @type {NodeListOf<HTMLElement>|null} */ #dots = null;
  /** @type {number} */ #current = 0;
  /** @type {ReturnType<typeof setInterval>|null} */ #timer = null;

  connectedCallback() {
    requestAnimationFrame(() => this.#init());
  }

  #init() {
    this.#track = this.querySelector('[data-track]');
    this.#dots  = this.querySelectorAll('[data-dot]');

    if (!this.#track || this.#dots.length < 2) return;

    this.#dots.forEach((dot, i) => {
      dot.addEventListener('click', () => this.#goTo(i));
    });

    this.#track.addEventListener('scroll', () => this.#onScroll(), { passive: true });

    if (!window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      this.#timer = setInterval(() => {
        if (this.#dots) this.#goTo((this.#current + 1) % this.#dots.length);
      }, 5000);
    }
  }

  #onScroll() {
    if (!this.#track || !this.#dots) return;
    const slideWidth = this.#track.scrollWidth / this.#dots.length;
    const index = Math.round(this.#track.scrollLeft / slideWidth);
    if (index === this.#current) return;
    this.#current = index;
    this.#dots.forEach((d, i) => d.classList.toggle('is-active', i === index));
  }

  disconnectedCallback() {
    if (this.#timer) clearInterval(this.#timer);
  }

  /** @param {number} index */
  #goTo(index) {
    if (!this.#track || !this.#dots) return;
    this.#current = index;
    const slideWidth = this.#track.scrollWidth / this.#dots.length;
    this.#track.scrollTo({ left: slideWidth * index, behavior: 'smooth' });
    this.#dots.forEach((d, i) => d.classList.toggle('is-active', i === index));
  }

  onSectionLoad()   { /* theme editor hook */ }
  onSectionUnload() { this.disconnectedCallback(); }
}

if (!customElements.get('sidebar-slider')) {
  customElements.define('sidebar-slider', SidebarSlider);
}
